import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Modal, Button } from "react-bootstrap";
import {
  getDataAPI,
  postDataAPI,
  deleteDataAPI,
} from "../../../services/apiWrapper";
import { ALERT } from "../../../store/actions/actionTypes";
import CourseCascade from "./CourseCascade";

/**
 * LMS - Course Content (Council/Admin + Faculty).
 *
 * Pick a course, then a subject; manage its chapters, each chapter's lectures
 * (YouTube), and the subject's study material. A faculty only sees the courses
 * assigned to them (the server scopes /courses and re-checks every write).
 */
export default function CourseContent() {
  const dispatch = useDispatch();
  const token = () => localStorage.getItem("adminToken");
  const alert = (payload) => dispatch({ type: ALERT, payload });

  const [subjects, setSubjects] = useState([]);
  const [courseId, setCourseId] = useState("");
  const [courseObj, setCourseObj] = useState(null);
  const [subjectId, setSubjectId] = useState("");
  const [tree, setTree] = useState({ chapters: [], materials: [] });

  const isFlat = courseObj && String(courseObj.course_type) === "1"; // exam-wise

  // chapter modal
  const [chShow, setChShow] = useState(false);
  const [chForm, setChForm] = useState({ id: null, title: "", term: 0, sort_order: 0 });
  // lecture modal
  const [lecShow, setLecShow] = useState(false);
  const [lecForm, setLecForm] = useState({ id: null, chapter_id: null, title: "", youtube: "", sort_order: 0 });
  // material modal
  const [matShow, setMatShow] = useState(false);
  const [matForm, setMatForm] = useState({ title: "", chapter_id: "", file: null });

  const loadSubjects = async (cid) => {
    setSubjects([]);
    setSubjectId("");
    setTree({ chapters: [], materials: [] });
    if (!cid) return;
    try {
      const res = await dispatch(getDataAPI(`admin/lms/content/subjects/${cid}`, token()));
      setSubjects(res.data.data || []);
    } catch (e) {
      alert({ error: "Could not load subjects." });
    }
  };

  const loadTree = async (cid, sid) => {
    if (!cid || !sid) return;
    try {
      const res = await dispatch(getDataAPI(`admin/lms/content/tree/${cid}/${sid}`, token()));
      setTree(res.data.data || { chapters: [], materials: [] });
    } catch (e) {
      alert({ error: "Could not load content." });
    }
  };

  const onCourse = (id, obj) => {
    setCourseId(id);
    setCourseObj(obj);
    loadSubjects(id);
  };
  const onSubject = (e) => {
    setSubjectId(e.target.value);
    loadTree(courseId, e.target.value);
  };

  /* ---- chapter ---- */
  const openChapter = (ch) => {
    const subj = subjects.find((s) => String(s.id) === String(subjectId));
    setChForm(
      ch
        ? { id: ch.id, title: ch.title, term: ch.term, sort_order: ch.sort_order }
        : { id: null, title: "", term: isFlat ? 0 : Number(subj?.semester || 1), sort_order: (tree.chapters.length || 0) + 1 }
    );
    setChShow(true);
  };
  const saveChapter = async (e) => {
    e.preventDefault();
    try {
      await dispatch(
        postDataAPI(
          "admin/lms/content/chapter",
          { ...chForm, course_id: courseId, subject_id: subjectId },
          token()
        )
      );
      alert({ success: "Chapter saved." });
      setChShow(false);
      loadTree(courseId, subjectId);
    } catch (err) {
      alert({ error: err.response?.data?.message || "Could not save chapter." });
    }
  };
  const deleteChapter = async (ch) => {
    if (!window.confirm(`Delete chapter "${ch.title}" and its lectures?`)) return;
    try {
      await dispatch(deleteDataAPI(`admin/lms/content/chapter/${ch.id}`, token()));
      alert({ success: "Chapter removed." });
      loadTree(courseId, subjectId);
    } catch (err) {
      alert({ error: err.response?.data?.message || "Could not delete." });
    }
  };

  /* ---- lecture ---- */
  const openLecture = (chapterId, lec) => {
    setLecForm(
      lec
        ? { id: lec.id, chapter_id: chapterId, title: lec.title, youtube: lec.youtube_id, sort_order: lec.sort_order }
        : { id: null, chapter_id: chapterId, title: "", youtube: "", sort_order: 0 }
    );
    setLecShow(true);
  };
  const saveLecture = async (e) => {
    e.preventDefault();
    try {
      await dispatch(postDataAPI("admin/lms/content/lecture", lecForm, token()));
      alert({ success: "Lecture saved." });
      setLecShow(false);
      loadTree(courseId, subjectId);
    } catch (err) {
      alert({ error: err.response?.data?.message || "Could not save lecture." });
    }
  };
  const deleteLecture = async (lec) => {
    if (!window.confirm(`Delete lecture "${lec.title}"?`)) return;
    try {
      await dispatch(deleteDataAPI(`admin/lms/content/lecture/${lec.id}`, token()));
      alert({ success: "Lecture removed." });
      loadTree(courseId, subjectId);
    } catch (err) {
      alert({ error: err.response?.data?.message || "Could not delete." });
    }
  };

  /* ---- material (multipart) ---- */
  const openMaterial = () => {
    setMatForm({ title: "", chapter_id: "", file: null });
    setMatShow(true);
  };
  const saveMaterial = async (e) => {
    e.preventDefault();
    if (!matForm.file) {
      alert({ error: "Please choose a file." });
      return;
    }
    try {
      const fd = new FormData();
      fd.append("course_id", courseId);
      fd.append("subject_id", subjectId);
      if (matForm.chapter_id) fd.append("chapter_id", matForm.chapter_id);
      fd.append("title", matForm.title);
      fd.append("file", matForm.file);
      // use the api wrapper's post (axios) so the adminToken header is set
      await dispatch(postDataAPI("admin/lms/content/material", fd, token()));
      alert({ success: "Material added." });
      setMatShow(false);
      loadTree(courseId, subjectId);
    } catch (err) {
      alert({ error: err.response?.data?.message || "Could not add material." });
    }
  };
  const deleteMaterial = async (m) => {
    if (!window.confirm(`Delete material "${m.title}"?`)) return;
    try {
      await dispatch(deleteDataAPI(`admin/lms/content/material/${m.id}`, token()));
      alert({ success: "Material removed." });
      loadTree(courseId, subjectId);
    } catch (err) {
      alert({ error: err.response?.data?.message || "Could not delete." });
    }
  };

  return (
    <div className="container-fluid" style={{ padding: 20 }}>
      <h4 className="mb-3">Course Content</h4>

      <CourseCascade onCourse={onCourse} />

      <div className="row mb-3">
        <div className="col-md-5">
          <label>Subject</label>
          <select className="form-control" value={subjectId} onChange={onSubject} disabled={!courseId}>
            <option value="">— select subject —</option>
            {subjects.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} {s.code ? `(${s.code})` : ""}
              </option>
            ))}
          </select>
        </div>
      </div>

      {subjectId ? (
        <>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 style={{ margin: 0 }}>Chapters</h5>
            <Button size="sm" variant="primary" onClick={() => openChapter(null)}>
              + Add Chapter
            </Button>
          </div>

          {tree.chapters.length === 0 ? (
            <p className="text-muted">No chapters yet.</p>
          ) : (
            tree.chapters.map((ch) => (
              <div key={ch.id} className="card mb-3">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <b>
                    {ch.title}{" "}
                    {!isFlat && ch.term ? <small className="text-muted">(Term {ch.term})</small> : null}
                  </b>
                  <span>
                    <Button size="sm" variant="outline-primary" className="mr-2" onClick={() => openLecture(ch.id, null)}>
                      + Lecture
                    </Button>{" "}
                    <Button size="sm" variant="outline-secondary" className="mr-2" onClick={() => openChapter(ch)}>
                      Edit
                    </Button>{" "}
                    <Button size="sm" variant="outline-danger" onClick={() => deleteChapter(ch)}>
                      Delete
                    </Button>
                  </span>
                </div>
                <div className="card-body">
                  {(ch.lectures || []).length === 0 ? (
                    <span className="text-muted">No lectures.</span>
                  ) : (
                    <table className="table table-sm mb-0">
                      <tbody>
                        {ch.lectures.map((lec) => (
                          <tr key={lec.id}>
                            <td style={{ width: 40 }}>{lec.sort_order}</td>
                            <td>{lec.title}</td>
                            <td style={{ width: 150 }}>
                              <code>{lec.youtube_id}</code>
                            </td>
                            <td style={{ width: 150 }}>
                              <Button size="sm" variant="outline-primary" className="mr-2" onClick={() => openLecture(ch.id, lec)}>
                                Edit
                              </Button>{" "}
                              <Button size="sm" variant="outline-danger" onClick={() => deleteLecture(lec)}>
                                Delete
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            ))
          )}

          <div className="d-flex justify-content-between align-items-center mb-2 mt-4">
            <h5 style={{ margin: 0 }}>Study Material</h5>
            <Button size="sm" variant="primary" onClick={openMaterial}>
              + Add Material
            </Button>
          </div>
          {tree.materials.length === 0 ? (
            <p className="text-muted">No study material yet.</p>
          ) : (
            <table className="table table-bordered bg-white">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>File</th>
                  <th style={{ width: 120 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {tree.materials.map((m) => (
                  <tr key={m.id}>
                    <td>{m.title}</td>
                    <td>
                      <a href={m.file} target="_blank" rel="noreferrer">
                        view
                      </a>
                    </td>
                    <td>
                      <Button size="sm" variant="outline-danger" onClick={() => deleteMaterial(m)}>
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      ) : (
        <p className="text-muted">Choose a course and subject to manage its content.</p>
      )}

      {/* Chapter modal */}
      <Modal show={chShow} onHide={() => setChShow(false)}>
        <form onSubmit={saveChapter}>
          <Modal.Header closeButton>
            <Modal.Title>{chForm.id ? "Edit Chapter" : "Add Chapter"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="form-group">
              <label>Chapter title</label>
              <input className="form-control" value={chForm.title} onChange={(e) => setChForm({ ...chForm, title: e.target.value })} required />
            </div>
            {!isFlat && (
              <div className="form-group">
                <label>Semester / Year number</label>
                <input type="number" min="0" className="form-control" value={chForm.term} onChange={(e) => setChForm({ ...chForm, term: e.target.value })} />
              </div>
            )}
            <div className="form-group">
              <label>Order</label>
              <input type="number" min="0" className="form-control" value={chForm.sort_order} onChange={(e) => setChForm({ ...chForm, sort_order: e.target.value })} />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setChShow(false)}>Cancel</Button>
            <Button variant="primary" type="submit">Save</Button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Lecture modal */}
      <Modal show={lecShow} onHide={() => setLecShow(false)}>
        <form onSubmit={saveLecture}>
          <Modal.Header closeButton>
            <Modal.Title>{lecForm.id ? "Edit Lecture" : "Add Lecture"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="form-group">
              <label>Lecture title</label>
              <input className="form-control" value={lecForm.title} onChange={(e) => setLecForm({ ...lecForm, title: e.target.value })} required />
            </div>
            <div className="form-group">
              <label>YouTube link (or video id)</label>
              <input className="form-control" value={lecForm.youtube} onChange={(e) => setLecForm({ ...lecForm, youtube: e.target.value })} placeholder="https://www.youtube.com/watch?v=..." required />
              <small className="text-muted">Only the video id is stored; students watch it inside the LMS.</small>
            </div>
            <div className="form-group">
              <label>Order</label>
              <input type="number" min="0" className="form-control" value={lecForm.sort_order} onChange={(e) => setLecForm({ ...lecForm, sort_order: e.target.value })} />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setLecShow(false)}>Cancel</Button>
            <Button variant="primary" type="submit">Save</Button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Material modal */}
      <Modal show={matShow} onHide={() => setMatShow(false)}>
        <form onSubmit={saveMaterial}>
          <Modal.Header closeButton>
            <Modal.Title>Add Study Material</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="form-group">
              <label>Title</label>
              <input className="form-control" value={matForm.title} onChange={(e) => setMatForm({ ...matForm, title: e.target.value })} required />
            </div>
            <div className="form-group">
              <label>Attach to chapter (optional)</label>
              <select className="form-control" value={matForm.chapter_id} onChange={(e) => setMatForm({ ...matForm, chapter_id: e.target.value })}>
                <option value="">— subject level —</option>
                {tree.chapters.map((c) => (
                  <option key={c.id} value={c.id}>{c.title}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>File (PDF / notes)</label>
              <input type="file" className="form-control-file" onChange={(e) => setMatForm({ ...matForm, file: e.target.files[0] })} required />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setMatShow(false)}>Cancel</Button>
            <Button variant="primary" type="submit">Upload</Button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
}
